import { useEffect } from 'react';
import './App.css';
import FullScreenLoader from './Components/loader';
import Toast from './Components/toast';
import AppRouting from './Routes';
import useGeneralStore from './Store/generalStore';
import useScrollToTop from './Config/hooks/useScrollToTop';
import useCountryStore from './Store/countryStore';
import useCountryDetection from './Config/hooks/useCountryDetection';
import useCurrency from './Config/hooks/useCurrency';
import CountrySelectionModal from './Components/countrySelectionModal';
import { activatePartnerFlow, hasPartnerUrlParam, isPartnerFlowActive } from './Config/utils/partnerFlowUtils';
import { useLocation } from 'react-router-dom';

function App() {
  const location = useLocation();
  const isPartnerDeeplink = isPartnerFlowActive();

  const { loading } = useGeneralStore((state) => state)
  const selectedCountry = useCountryStore((state) => state.selectedCountry);
  const initializeCountry = useCountryStore((state) => state.initializeCountry);
  const setDetectedCountry = useCountryStore((state) => state.setDetectedCountry);
  const setSelectedCountry = useCountryStore((state) => state.setSelectedCountry);
  const isCountryModalOpen = useCountryStore((state) => state.isCountryModalOpen);
  const openCountryModal = useCountryStore((state) => state.openCountryModal);
  const closeCountryModal = useCountryStore((state) => state.closeCountryModal);
  
  const { detectedCountry, isLoading: isDetectingCountry, error: detectionError } = useCountryDetection();
  useCurrency();

  // on every path changes it will scroll to top.
  useScrollToTop();

  // Initialize country from sessionStorage on mount (skip for partner deeplink flows)
  useEffect(() => {
    if (isPartnerDeeplink) return;
    initializeCountry();
  }, [initializeCountry, isPartnerDeeplink]);

  // Partner deeplink: persist flow for the session and never prompt for country selection
  useEffect(() => {
    if (hasPartnerUrlParam()) {
      activatePartnerFlow();
    }
    if (isPartnerFlowActive()) {
      closeCountryModal();
    }
  }, [location.search, closeCountryModal]);

  // When API returns current country: set it in store so "I live in" and popup dropdown show it automatically
  useEffect(() => {
    if (isPartnerDeeplink) return;

    if (detectedCountry) {
      setDetectedCountry(detectedCountry);
      // If no country is selected yet (e.g. first visit), set it from API so both dropdowns show it
      if (!selectedCountry) {
        setSelectedCountry(detectedCountry);
      }
    }
  }, [isPartnerDeeplink, detectedCountry, selectedCountry, setDetectedCountry, setSelectedCountry]);

  // Show modal if country detection fails, returns null, or has error and no country is selected
  useEffect(() => {
    if (isPartnerDeeplink) return;

    if (!isDetectingCountry && (!detectedCountry || detectionError) && !selectedCountry) {
      const timer = setTimeout(() => {
        openCountryModal();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isPartnerDeeplink, isDetectingCountry, detectedCountry, detectionError, selectedCountry, openCountryModal]);

  return (
    <div>
      {loading && <FullScreenLoader />}
      <Toast />
      <AppRouting />
      <CountrySelectionModal 
        open={isPartnerDeeplink ? false : isCountryModalOpen}
        onClose={closeCountryModal}
        detectedCountry={detectedCountry}
      />
    </div>
  );
}

export default App;
